import { niceNumber } from "./Util";
import type {
    IGetLatestWorkforceKpiReportRes,
    IWorkforceKpiCompetenceRow,
    IWorkforceKpiLevelMetrics,
} from "./backend/reports.generated.types";

export type KpiValueStatus = "warning" | "danger";
export type KpiUnit = "h";

type StringValue = {
    value: string | null;
};

export type WorkforceKpiValue = StringValue & {
    unit?: KpiUnit;
    isRatio?: boolean;
    status?: KpiValueStatus;
};

/** One metric row inside a `WorkforceKpi` card (`id` matches translation keys under `Manufacturing.WorkforceKPIs.field.*`). */
export type WorkforceKpiMetric = WorkforceKpiValue & {
    id: string;
};

export type WorkforceKpi = {
    id: string;
    kpis: WorkforceKpiMetric[];
    status?: KpiValueStatus;
};

export const COMPETENCE_TABLE_KPIS = [
    "active_shifts",
    "required_workers",
    "required_workers_hours",
    "scheduled_workers",
    "scheduled_workers_pct",
] as const;

/** Column ids for the competence table header row: competence, then `COMPETENCE_TABLE_KPIS` in order. */
export const COMPETENCE_TABLE_COLUMN_IDS = ["competence", ...COMPETENCE_TABLE_KPIS] as const;

export type CompetenceTableKpi = (typeof COMPETENCE_TABLE_KPIS)[number];
export type CompetenceTableColumnId = (typeof COMPETENCE_TABLE_COLUMN_IDS)[number];
export type CompetenceTableRow = [StringValue, ...WorkforceKpiValue[]];

export type ViewModel = {
    createdAt: string;
    startDate: string;
    endDate: string;
    timeRangeKeys: string[];
    plant: {
        uuid: string;
        title: string;
        externalId: string;
        kpis: WorkforceKpi[];
        competenceTable: CompetenceTableViewModel;
        workshops: WorkshopKpisViewModel[];
    };
};

export type WorkshopKpisViewModel = {
    uuid: string;
    title: string;
    externalId: string;
    kpis: WorkforceKpi[];
    competenceTable: CompetenceTableViewModel;
};

export type CompetenceTableViewModel = {
    header: CompetenceTableColumnId[];
    body: CompetenceTableRow[];
};

const HOURS_UNIT = "h";

/**
 * Formats a number to a string with 1 decimal place.
 */
export const formatKpiNumber = (n: number | undefined | null): string | null => {
    if (n == null) {
        return null;
    }
    return niceNumber(n, 1);
};

/**
 * Returns a UI hint for scheduled workers coverage %:
 * - below 90% warning
 * - below 80% danger
 */
export const getStatusForPct = (pct: number): KpiValueStatus | undefined => {
    if (pct < 80) {
        return "danger";
    }
    if (pct < 90) {
        return "warning";
    }
    return undefined;
};

/**
 * Maps plant- or workshop-level metrics to KPI cards.
 * Use `id` and nested keys as translation ids, e.g. `Manufacturing.WorkforceKPIs.row.${id}`.
 */
const mapLevelKpis = (level: IWorkforceKpiLevelMetrics): WorkforceKpi[] => {
    return [
        {
            id: "active_shifts",
            kpis:
                // TOTEST:
                // Is this case (undefined/null) possible?
                // Can it happen that there is no data?
                // How is it modeled in that case?
                // Are zero values always legit or the indication of an error?
                level.active_shifts_count == null && level.active_shifts_hours == null
                    ? []
                    : [
                          { id: "count", value: formatKpiNumber(level.active_shifts_count) },
                          { id: "hours", value: formatKpiNumber(level.active_shifts_hours), unit: HOURS_UNIT },
                      ],
        },
        {
            id: "required_workers",
            kpis:
                level.required_workers_workers == null && level.required_workers_hours == null
                    ? []
                    : [
                          { id: "workers", value: formatKpiNumber(level.required_workers_workers) },
                          { id: "hours", value: formatKpiNumber(level.required_workers_hours), unit: HOURS_UNIT },
                      ],
        },
        {
            id: "scheduled_workers",
            status: getStatusForPct(level.scheduled_workers_ratio_pct),
            kpis:
                level.scheduled_workers_count == null && level.scheduled_workers_ratio_pct == null
                    ? []
                    : [
                          { id: "count", value: formatKpiNumber(level.scheduled_workers_count) },
                          {
                              id: "ratioPct",
                              value: formatKpiNumber(level.scheduled_workers_ratio_pct),
                              isRatio: true,
                          },
                      ],
        },
        {
            id: "scheduled_work",
            kpis:
                level.scheduled_work_shifts == null && level.scheduled_work_ratio_pct == null
                    ? []
                    : [
                          { id: "shifts", value: formatKpiNumber(level.scheduled_work_shifts) },
                          { id: "ratioPct", value: formatKpiNumber(level.scheduled_work_ratio_pct), isRatio: true },
                      ],
        },
    ];
};

/**
 * Maps `competence_table` to `{ header, body } view model`
 * - `header` is column ids (`COMPETENCE_TABLE_COLUMN_IDS`)
 * - each `body` row is `[competence cell, ...kpiValues]` in `COMPETENCE_TABLE_KPIS` order.
 */
const mapCompetencesKpis = (competenceTable: IWorkforceKpiCompetenceRow[]): CompetenceTableViewModel => {
    return {
        header: [...COMPETENCE_TABLE_COLUMN_IDS],
        body: competenceTable.map(
            (row): CompetenceTableRow => [
                { value: row.competence },
                { value: formatKpiNumber(row.active_shifts_count) },
                { value: formatKpiNumber(row.required_workers_workers) },
                { value: formatKpiNumber(row.required_workers_hours), unit: HOURS_UNIT },
                { value: formatKpiNumber(row.scheduled_workers_count) },
                {
                    value: formatKpiNumber(row.scheduled_workers_ratio_pct),
                    isRatio: true,
                    status: getStatusForPct(row.scheduled_workers_ratio_pct),
                },
            ]
        ),
    };
};

/**
 * Maps a report to a view model for a given timeRangeKey.
 *
 * Returns general and competences KPIs at the plant and workshops levels.
 * Returns null if the report or time range key is not found.
 */
export const mapReportToViewModel = (
    report: IGetLatestWorkforceKpiReportRes,
    timeRangeKey: string
): ViewModel | null => {
    const plant = report.plants[0];
    if (!plant) {
        return null;
    }

    const data = plant.time_ranges[timeRangeKey];
    if (!data) {
        return null;
    }

    return {
        createdAt: report.metadata.created_at,
        startDate: data.start_date,
        endDate: data.end_date,
        timeRangeKeys: report.metadata.time_range_keys ?? [],
        plant: {
            uuid: plant.uuid,
            title: plant.title,
            externalId: plant.external_id,
            kpis: mapLevelKpis(data.plant),
            competenceTable: mapCompetencesKpis(data.plant.competence_table ?? []),
            workshops: data.workshops.map(workshop => ({
                uuid: workshop.uuid,
                title: workshop.title,
                externalId: workshop.external_id,
                kpis: mapLevelKpis(workshop),
                competenceTable: mapCompetencesKpis(workshop.competence_table ?? []),
            })),
        },
    };
};
